import React, { FunctionComponent, useEffect, useState, KeyboardEvent } from 'react';
import { debounce } from 'debounce';
import { always, any, append, filter, find, map, pipe, split } from 'ramda';
import Highlighter from 'react-highlight-words';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { ActionMeta, IndicatorProps, MultiValueProps, OptionProps, OptionsType, ValueType } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { IReportData } from '../../common/models/candidate/IReportData';
import { IAppState } from '../../common/models/store/IAppState';
import { IReportSelectedUser } from '../../common/models/user/IReportSelectedUser';
import { IReportUser } from '../../common/models/user/IReportUser';
import { ISearchUser } from '../../common/models/user/ISearchUser';
import { User } from '../../common/models/user/User';
import { validateEmail } from '../../common/validation/yup';
import Icon from '../../components/Icon';
import RegularButton from '../../components/RegularButton';
import UserLogo from '../../components/UserLogo';
import { getReportRoutine, searchReportUsersRoutine, setReportCandidatesRoutine } from './routines';
import { getUser, selectStyles } from './utils';
import { IUserLogoInfo } from '../../common/models/user/IUserLogoInfo';
import styles from './styles.module.scss';

interface IProps {
  candidateIds: string[];
  user: User;
  loading: boolean;
  users: ISearchUser[];
  searchUsers: IBindingCallback1<string>;
  getReport: IBindingCallback1<IReportData>;
  closeReportModal: IBindingAction;
}

interface IOptionType {
  data: IReportSelectedUser;
  value: string;
  isFixed?: boolean;
}

const ShareReportWith: FunctionComponent<IProps> = ({
  users,
  candidateIds,
  user,
  loading,
  getReport,
  searchUsers,
  closeReportModal
}) => {
  const defaultOption: IReportSelectedUser = {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    initials: user.initials
  };
  const [selected, setSelected] = useState<IReportSelectedUser[]>([defaultOption]);
  const [searchVal, setSearchVal] = useState('');
  const search = debounce((val: string) => searchUsers(val), 500);

  useEffect(() => {
    search(searchVal);
    return () => search.clear();
  }, [searchVal]);

  useEffect(() => () => setSearchVal(''), []);

  const trimmedEmail = searchVal.trim();

  const getUserByEmail = () => find(({ email }) => email === trimmedEmail, users);

  const keyHandler = (event: KeyboardEvent<Element>) => {
    if (event.key === ' ' && validateEmail(trimmedEmail)) {
      const userData = getUserByEmail();
      const isSelected = any(({ email }) => email === trimmedEmail, selected);
      if (!isSelected) {
        setSelected(append(userData || { email: trimmedEmail }, selected));
      }
      setSearchVal('');
    }
  };

  const sendReport = (data: IReportData) => {
    getReport(data);
    closeReportModal();
  };

  const mapSelectedData = () => pipe<IReportSelectedUser[], IReportSelectedUser[], IReportUser[]>(
    filter(({ id }: IReportSelectedUser) => id !== user.id),
    map(({ email, id, firstName, lastName }) => ({ email, id, firstName, lastName }))
  )(selected);

  const mapToOptions = (options: IReportSelectedUser[]) => map(item => ({
    value: item.email,
    data: item,
    isFixed: user.id === item.id
  }), options);

  const onChange = (value: ValueType<IOptionType>, { removedValue }: ActionMeta<IOptionType>) => {
    if (removedValue?.isFixed) {
      return;
    }

    setSelected((value as OptionsType<IOptionType>)?.length
      ? map(({ data }) => data, value as IOptionType[])
      : [defaultOption]);
  };

  const CustomOption = ({
    innerRef,
    innerProps,
    data: { data, __isNew__ },
    isFocused
  }: OptionProps<IOptionType>) => (
    <div ref={innerRef} {...innerProps} className={`${styles.listItem} ${isFocused ? styles.focusedListItem : ''}`}>
      {__isNew__
        ? `Add "${searchVal}"`
        : (
          <>
            <UserLogo hideTooltip user={getUser(data) as IUserLogoInfo} size="2rem" />
            <div className="ml-3">
              {data.firstName && (
              <>
                <strong>
                  <Highlighter
                    textToHighlight={`${data.firstName} ${data.lastName}`}
                    searchWords={split(' ', searchVal)}
                    autoEscape
                    highlightClassName="highlighted-text"
                  />
                </strong>
                <br />
              </>
              )}
              <Highlighter
                textToHighlight={data.email}
                searchWords={split(' ', searchVal)}
                autoEscape
                highlightClassName="highlighted-text"
              />
            </div>
          </>
        )}
    </div>
  );

  const CustomMultiValue = ({ innerProps, removeProps, data: { data, isFixed } }: MultiValueProps<IOptionType>) => (
    <div {...innerProps} className={styles.shareWithItem}>
      <UserLogo hideTooltip user={getUser(data) as IUserLogoInfo} size="2rem" />
      <span className="ml-2">{data.firstName ? `${data.firstName} ${data.lastName}` : data.email}</span>
      {isFixed
        ? (
          <>
            &nbsp;(
            <FormattedMessage id="me" defaultMessage="me" />
            )
          </>
        )
        : <div className="ml-4" {...removeProps}><Icon className={styles.deleteBtn} icon="times" /></div>}
    </div>
  );

  const CustomClearIndicator = ({ innerProps }: IndicatorProps<IOptionType>) => (
    <div {...innerProps}><Icon className={styles.deleteBtn} icon="times" /></div>
  );

  return (
    <div className={styles.modalBody}>
      <header className={`custom-modal-header ${styles.modalHeader}`}>
        <FormattedMessage id="reportPopup.title" defaultMessage="Email Candidate Report" />
      </header>
      <main className={styles.main}>
        <div className={styles.label}>
          <FormattedMessage id="surveyBuilder.sendTo" defaultMessage="Send to" />
        </div>
        <CreatableSelect
          components={{
            Option: CustomOption,
            MultiValue: CustomMultiValue,
            IndicatorSeparator: () => null,
            DropdownIndicator: () => null,
            ClearIndicator: CustomClearIndicator
          }}
          options={mapToOptions(users)}
          onChange={onChange}
          isMulti
          hideSelectedOptions
          inputValue={searchVal}
          onInputChange={setSearchVal}
          isValidNewOption={val => validateEmail(val) && !getUserByEmail()}
          value={mapToOptions(selected)}
          onKeyDown={keyHandler}
          isLoading={loading}
          createOptionPosition="first"
          onCreateOption={val => setSelected(append({ email: val }, selected))}
          isClearable={selected.length > 1}
          styles={selectStyles}
          filterOption={always(true)}
        />
      </main>
      <footer className={`modal-footer-bttns ${styles.modalFooter}`}>
        <RegularButton
          onClick={() => closeReportModal()}
          variant="tertiary"
          textIntlId="cancel"
          defaultMessage="Cancel"
        />
        <RegularButton
          variant="secondary"
          textIntlId="reportPopup.download"
          defaultMessage="🡐 Download instead"
          icon={<Icon icon="download" />}
          onClick={() => sendReport({ candidateIds, sendByEmail: false })}
        />
        <RegularButton
          textIntlId="send"
          defaultMessage="Send"
          icon={<Icon icon="paperPlane" />}
          onClick={() => sendReport({ candidateIds, sendByEmail: true, users: mapSelectedData() })}
        />
      </footer>
    </div>
  );
};

const mapStateToProps = ({
  user: { user }, report: { users, candidateIds, loading }
}: IAppState) => ({ user: user as User, users, candidateIds: candidateIds as string[], loading });

const mapDispachToProps = {
  getReport: getReportRoutine,
  searchUsers: searchReportUsersRoutine,
  closeReportModal: setReportCandidatesRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ShareReportWith);
